// frontend/src/pages/PrivacyPolicyPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="content-container px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/fastel-logo.png" 
                alt="Fastel" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                Fastel
              </span>
            </div>
            <Link to="/" className="text-sm text-gray-600 hover:text-primary-600 flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="content-container px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Privacy Policy</h1>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-6 text-sm text-gray-600">
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">1. Introduction</h2>
              <p>
                Fastel Telecom LTD ("we", "our", "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.
              </p>
              <p className="mt-2">
                By using Fastel, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">2. Information We Collect</h2>
              <div className="space-y-2">
                <p><strong>Personal Information:</strong> Name, email address, phone number, profile picture, and business information.</p>
                <p><strong>Transaction Information:</strong> Airtime/data purchases, bill payments, wallet transactions, and payment history.</p>
                <p><strong>Business Information:</strong> Business name, address, category, description, products, services, and contact details provided by users.</p>
                <p><strong>Content Information:</strong> Posts, comments, messages, and other content you create on the platform.</p>
                <p><strong>Usage Data:</strong> How you interact with our app, including features used and time spent.</p>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">3. How We Use Your Information</h2>
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To process your transactions (airtime, data, bills, wallet funding)</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To manage your account and provide customer support</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To improve our services and user experience</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To send you transaction confirmations and updates</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To display your business listings in the marketplace</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>To prevent fraud and ensure platform security</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>For promotional purposes, including showcasing user content</span>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">4. Information Sharing</h2>
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong>Payment Processors:</strong> Monnify and Paystack to process wallet funding and payments</li>
                <li><strong>Service Providers:</strong> Firebase for notifications, Cloudinary for image storage, and Resend for emails</li>
                <li><strong>VTU Providers:</strong> Alrahuzdata for processing airtime, data, and bill payments</li>
                <li><strong>Law Enforcement:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="mt-2">
                All third-party providers have their own privacy policies and are contractually obligated to protect your data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">5. Data Security</h2>
              <p>
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>256-bit encryption for all data transmission</li>
                <li>Secure servers with firewalls</li>
                <li>Access controls to protect your data</li>
                <li>Regular security updates</li>
              </ul>
              <p className="mt-2">
                Your data is stored on secure servers hosted on Railway, with both server and database in secure environments.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">6. Your Rights</h2>
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access your personal data anytime from your profile</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Request correction of inaccurate data</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Request account deletion by contacting support</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Opt-out of promotional communications</span>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">7. Data Retention</h2>
              <p>
                We retain your information for as long as your account is active or as needed to provide services. 
                You may request account deletion through our support team. Transaction history is retained permanently 
                for record-keeping and audit purposes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">8. Children's Privacy</h2>
              <p>
                Fastel is not specifically designed for children. We do not knowingly collect personal information 
                from individuals under the age of 13. If you are a parent or guardian and believe your child has 
                provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">9. Third-Party Links</h2>
              <p>
                Our app may contain links to third-party websites (e.g., business social media profiles). 
                We are not responsible for the privacy practices of these sites.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of any changes by posting 
                the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">11. Contact Us</h2>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-primary-600" />
                  <span><strong>Email:</strong> fasteltelecomltd@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-primary-600" />
                  <span><strong>Phone:</strong> 08109650254, 09042183619</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-primary-600" />
                  <span><strong>Address:</strong> NO. 35, MIJINYAWA STREET TUDUN JUKUN, ZARIA, KADUNA STATE, NIGERIA</span>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
            <p>&copy; {new Date().getFullYear()} Fastel Telecom LTD. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;